// ============================================================
// XSS PREVENTION UTILITIES
// ============================================================

/**
 * Strips dangerous characters and patterns from a string value.
 * Removes: HTML tags, javascript: / data: URIs, event handlers,
 * script-like keywords, and common URL injection patterns.
 */
function sanitizeValue(value) {
  if (typeof value !== 'string') return value;

  return value
    // Remove all HTML tags
    .replace(/<[^>]*>/gi, '')
    // Remove javascript:, data:, vbscript: URI schemes
    .replace(/\b(javascript|data|vbscript)\s*:/gi, '')
    // Remove inline event handlers (onclick=, onerror=, etc.)
    .replace(/on\w+\s*=\s*["']?[^"'>]*/gi, '')
    // Remove script/iframe/object/embed tags (extra safety)
    .replace(/<\s*(script|iframe|object|embed|form|input|link|meta)[^>]*>/gi, '')
    // Remove full URLs (http:// and https://) — safe for emails since they have no ://
    .replace(/https?:\/\/[^\s"'<>]*/gi, '')
    // Remove protocol-less URLs like //graph.org/... (but NOT email addresses)
    .replace(/\/\/[\w-]+\.[a-z]{2,}[^\s"'<>]*/gi, '')
    // Trim leading/trailing whitespace
    .trim();
}

/**
 * Sanitizes all text/email/tel/number/textarea fields in the form
 * before submission. Skips file inputs and hidden fields.
 */
function sanitizeForm(form) {
  const fields = form.querySelectorAll('input:not([type="file"]):not([type="hidden"]), select, textarea');

  fields.fields = form.querySelectorAll('input:not([type="file"]):not([type="hidden"]), select, textarea');

  form.querySelectorAll('input:not([type="file"]):not([type="hidden"]), textarea').forEach(field => {
    if (field.value && field.value.trim() !== 'Not Provided') {
      const cleaned = sanitizeValue(field.value);
      if (cleaned !== field.value) {
        console.warn(`[XSS] Sanitized field "${field.name}": "${field.value}" → "${cleaned}"`);
        field.value = cleaned;
      }
    }
  });
}

/**
 * Attaches a real-time sanitization listener to an input element.
 * Fires on 'blur' (when user leaves the field) to avoid disrupting typing.
 */
function attachSanitizationListener(input) {
  if (input.dataset.xssListenerAttached) return;

  input.addEventListener('blur', () => {
    if (input.type === 'file' || input.type === 'hidden') return;
    const cleaned = sanitizeValue(input.value);
    if (cleaned !== input.value) {
      console.warn(`[XSS] Real-time sanitized "${input.name}"`);
      input.value = cleaned;
    }
  });

  input.dataset.xssListenerAttached = 'true';
}

/**
 * Attach sanitization listeners to all current text/textarea inputs in scope.
 */
function attachAllSanitizationListeners(scope = document) {
  scope.querySelectorAll('input:not([type="file"]):not([type="hidden"]), textarea').forEach(attachSanitizationListener);
}


// ============================================================
// STATE
// ============================================================

let currentStep = 0;
let tradeInItemsCount = 1;
const formType = document
  .getElementById("quote-form")
  .dataset.formType;

const totalSteps = formType === "trade" ? 3 : 2;


// ============================================================
// DOM ELEMENTS
// ============================================================

const form = document.getElementById('quote-form');
const nextBtn = document.getElementById('next-btn');
const backBtn = document.getElementById('back-btn');
const nextBtnText = document.getElementById('next-btn-text');
const successScreen = document.getElementById('success-screen');
const tradeInItemsContainer = document.getElementById('trade-in-items-container');
const addItemBtn = document.getElementById('add-item-btn');


// ============================================================
// STEP SECTIONS
// ============================================================

const sections = [...document.querySelectorAll('.trade-form__section')];
const indicators = [...document.querySelectorAll('[data-step-indicator]')];


// ============================================================
// TRADE-IN ITEM HTML
// ============================================================

function createTradeInItemHTML(index, data = null) {
  const isFirst = index === 0;
  const photoLabels = ['Front', 'Back', 'Side', 'Box & Papers'];
  
  return `
  <div class="trade-form__card trade-in-item" data-index="${index}">
  <input type="hidden" name="---------- ${formType === 'trade' ? 'TRADE-IN WATCH' : 'WATCH DETAILS'} #${index + 1} ----------" value="">
      ${!isFirst ? `
        <button type="button" class="trade-form__btn--remove remove-item-btn">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>
        </button>
      ` : ''}
      
      <div class="trade-form__card-title">
        <div class="trade-form__card-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="7"/><polyline points="12 9 12 12 13.5 13.5"/><path d="M16.51 17.35 18.5 21"/><path d="M7.49 17.35 5.5 21"/><path d="M7.49 6.65 5.5 3"/><path d="M16.51 6.65 18.5 3"/></svg>
        </div>
        <h2>Trade-In Item ${tradeInItemsCount > 1 ? `#${index + 1}` : ''}</h2>
      </div>

      <div class="trade-form__grid trade-form__grid--2-cols">
        <div class="trade-form__field">
          <label class="trade-form__label">Brand <pre class="mandatory">*</pre></label>
          <input type="text" name="Watch ${index + 1} | Brand" required class="trade-form__input" placeholder="e.g. Omega" value="${data?.brand || ''}" oninput="this.value = this.value.replace(/[^A-Za-z\s'-]/g, '')" onpaste="if (!/^[A-Za-z\s'-]+$/.test((event.clipboardData || window.clipboardData).getData('text'))) event.preventDefault()">
        </div>
        <div class="trade-form__field">
          <label class="trade-form__label">Model <pre class="mandatory">*</pre></label>
          <input type="text" name="Watch ${index + 1} | Model" required class="trade-form__input" placeholder="e.g. Speedmaster" value="${data?.model || ''}" oninput="this.value = this.value.replace(/[^A-Za-z\s'-]/g, '')" onpaste="if (!/^[A-Za-z\s'-]+$/.test((event.clipboardData || window.clipboardData).getData('text'))) event.preventDefault()">
        </div>
        <div class="trade-form__field">
          <label class="trade-form__label">Reference Number</label>
          <input type="text" name="Watch ${index + 1} | Reference Number" class="trade-form__input" placeholder="e.g. 311.30.42.30.01.005" value="${data?.reference || ''}" oninput="this.value = this.value.replace(/[^A-Za-z\s'-]/g, '')" onpaste="if (!/^[A-Za-z\s'-]+$/.test((event.clipboardData || window.clipboardData).getData('text'))) event.preventDefault()">
        </div>
        <div class="trade-form__field">
          <label class="trade-form__label">Serial Number</label>
          <input type="text" name="Watch ${index + 1} | Serial Number" class="trade-form__input" placeholder="Found on case back or papers" value="${data?.serial || ''}" oninput="this.value = this.value.replace(/[^A-Za-z\s'-]/g, '')" onpaste="if (!/^[A-Za-z\s'-]+$/.test((event.clipboardData || window.clipboardData).getData('text'))) event.preventDefault()">
        </div>
        <div class="trade-form__field">
          <label class="trade-form__label">Condition <pre class="mandatory">*</pre></label>
          <select name="Watch ${index + 1} | Condition" class="trade-form__select" required>
            <option value="" disabled ${!data?.condition ? 'selected' : ''}>Select Option</option>
            <option ${data?.condition === 'Unworn' ? 'selected' : ''}>Unworn</option>
            <option ${data?.condition === 'Excellent' ? 'selected' : ''}>Excellent</option>
            <option ${data?.condition === 'Very Good' ? 'selected' : ''}>Very Good</option>
            <option ${data?.condition === 'Good' ? 'selected' : ''}>Good</option>
            <option ${data?.condition === 'Fair' ? 'selected' : ''}>Fair</option>
          </select>
        </div>
        <div class="trade-form__field">
          <label class="trade-form__label">Box & Papers <pre class="mandatory">*</pre></label>
          <select name="Watch ${index + 1} | Box & Papers" class="trade-form__select" required>
            <option value="" disabled ${!data?.boxPapers ? 'selected' : ''}>Select Option</option>
            <option ${data?.boxPapers === 'Both Box & Papers' ? 'selected' : ''}>Both Box & Papers</option>
            <option ${data?.boxPapers === 'Box Only' ? 'selected' : ''}>Box Only</option>
            <option ${data?.boxPapers === 'Papers Only' ? 'selected' : ''}>Papers Only</option>
            <option ${data?.boxPapers === 'Neither Box nor Papers' ? 'selected' : ''}>Neither Box nor Papers</option>
          </select>
        </div>

        <div class="trade-form__field">
          <label class="trade-form__label">What is your asking price?</label>

          <div style="display:flex; gap:12px;">
            
            <input 
              type="number"
              name="Watch ${index + 1} | Asking Price"
              class="trade-form__input trade-form__asking-price"
              placeholder="Enter amount"
              value="${data?.askingPrice || ''}"
              style="flex:2;"
              required
            >

            <select 
              name="Watch ${index + 1} | Currency"
              class="trade-form__select"
              style="flex:1;"
              required
            >
              <option value="" disabled ${!data?.currency ? 'selected' : ''}>Select <pre class="mandatory">*</pre></option>

              <option value="USD" selected>USD</option>
              <option value="AED" ${data?.currency === 'AED' ? 'selected' : ''}>AED</option>
              <option value="CHF" ${data?.currency === 'CHF' ? 'selected' : ''}>CHF</option>
              <option value="EUR" ${data?.currency === 'EUR' ? 'selected' : ''}>EUR</option>
              <option value="GBP" ${data?.currency === 'GBP' ? 'selected' : ''}>GBP</option>
              <option value="HKD" ${data?.currency === 'HKD' ? 'selected' : ''}>HKD</option>
              <option value="SGD" ${data?.currency === 'SGD' ? 'selected' : ''}>SGD</option>
            </select>

          </div>
        </div>
      </div>

      <div style="margin-top: 2.5rem;">
        <label class="trade-form__label" style="display: block; margin-bottom: 1rem;">Upload Photos</label>

        <div class="trade-form__photo-grid">
          <img src="https://cdn.shopify.com/s/files/1/0778/3451/8760/files/watches-front.jpg?v=1772535766" alt="Watch Front">
          <img src="https://cdn.shopify.com/s/files/1/0778/3451/8760/files/watches-back.jpg?v=1772535766" alt="Watch Back">
          <img src="https://cdn.shopify.com/s/files/1/0778/3451/8760/files/watches-side.jpg?v=1772535766" alt="Watch Side">
          <img src="https://cdn.shopify.com/s/files/1/0778/3451/8760/files/watches-box.jpg?v=1772535766" alt="Watch Box And Papers">
        </div>

        <div class="trade-form__photo-grid">
          ${photoLabels.map((label, i) => `
            <div class="trade-form__photo-box" onclick="this.querySelector('input').click()">
              <input type="file" name="Watch ${index + 1} | Photo | ${label}" accept="image/*" style="display: none;" class="photo-input">
              <div class="photo-preview-container">
                ${data?.photos?.[i] || `
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="color: var(--color-text-muted);"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                  <span class="trade-form__photo-label">${label}</span>
                `}
              </div>
            </div>
          `).join('')}
        </div>
        <p style="margin-top: 1rem; font-size: 14px; color: var(--color-text-muted); font-style: italic;">Photos help us provide a more accurate quote. Please include front, back, and side views.</p>
      </div>
    </div>
  `;
}

function captureTradeInData() {
  const items = [];

  document.querySelectorAll('.trade-in-item').forEach((item, index) => {
    const watchNumber = index + 1;

    const data = {
      brand: item.querySelector(`input[name="Watch ${watchNumber} | Brand"]`)?.value || '',
      model: item.querySelector(`input[name="Watch ${watchNumber} | Model"]`)?.value || '',
      reference: item.querySelector(`input[name="Watch ${watchNumber} | Reference Number"]`)?.value || '',
      serial: item.querySelector(`input[name="Watch ${watchNumber} | Serial Number"]`)?.value || '',
      condition: item.querySelector(`select[name="Watch ${watchNumber} | Condition"]`)?.value || '',
      boxPapers: item.querySelector(`select[name="Watch ${watchNumber} | Box & Papers"]`)?.value || '',
      askingPrice: item.querySelector(`input[name="Watch ${watchNumber} | Asking Price"]`)?.value || '',
      currency: item.querySelector(`select[name="Watch ${watchNumber} | Currency"]`)?.value || '',
      photos: Array.from(item.querySelectorAll('.photo-preview-container')).map(c => c.innerHTML)
    };

    items.push(data);
  });

  return items;
}

function attachPhotoListeners(scope = document) {
  scope.querySelectorAll('.photo-input').forEach((input) => {
    if (!input.dataset.listenerAttached) {
      input.addEventListener('change', (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        const container = e.target
          .closest('.trade-form__photo-box')
          .querySelector('.photo-preview-container');

        reader.onload = (event) => {
          container.innerHTML = `
            <img src="${event.target.result}" 
                 style="width:100%;height:100%;object-fit:cover;border-radius:0.8rem;">
          `;
        };

        reader.readAsDataURL(file);
      });

      input.dataset.listenerAttached = "true";
    }
  });
}

function renderTradeInItems(preserveData = true) {
  const savedData = preserveData ? captureTradeInData() : [];
  tradeInItemsContainer.innerHTML = '';
  
  for (let i = 0; i < tradeInItemsCount; i++) {
    const itemData = savedData[i] || null;
    tradeInItemsContainer.insertAdjacentHTML('beforeend', createTradeInItemHTML(i, itemData));
  }

  // Handle Add Button Visibility (Limit to 5)
  if (tradeInItemsCount >= 5) {
    addItemBtn.classList.add('component-hidden');
  } else {
    addItemBtn.classList.remove('component-hidden');
  }

  document.addEventListener('click', function (e) {
    const removeBtn = e.target.closest('.remove-item-btn');
    if (!removeBtn) return;

    const item = removeBtn.closest('.trade-in-item');
    if (item) {
      item.remove();
      tradeInItemsCount--;
    }
  });

  // Attach photo upload listeners
  attachPhotoListeners();

  // ✅ XSS: Attach real-time sanitization to all new inputs
  attachAllSanitizationListeners(tradeInItemsContainer);
}


// ============================================================
// NAVIGATION LOGIC
// ============================================================

function updateUI() {
  sections.forEach((section, idx) => {
    if (section) {
      if (idx === currentStep) {
        section.classList.remove('component-hidden');
      } else {
        section.classList.add('component-hidden');
      }
    }
  });

  indicators.forEach((indicator, idx) => {
    if (indicator) {
      if (idx === currentStep) {
        indicator.className = "trade-form__step trade-form__step--active";
      } else if (idx < currentStep) {
        indicator.className = "trade-form__step trade-form__step--completed";
        if (!indicator.querySelector('.lucide-check-circle')) {
          indicator.insertAdjacentHTML('beforeend', '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-check-circle" style="margin-left: 0.25rem;"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>');
        }
      } else {
        indicator.className = "trade-form__step";
        const check = indicator.querySelector('.lucide-check-circle');
        if (check) check.remove();
      }
    }
  });

  const isLastStep = currentStep === totalSteps - 1;

  if (currentStep === 0) {
    backBtn.classList.add('component-hidden');
  } else {
    backBtn.classList.remove('component-hidden');
  }

  if (isLastStep) {
    nextBtn.type = "submit";
    nextBtnText.textContent = "Get My Quote Now";
  } else {
    nextBtn.type = "button";

    if (currentStep === 0) {
      nextBtnText.textContent = "Continue to Looking For";
    } else {
      nextBtnText.textContent = "Continue to Trade-In Details";
    }
  }
}

function validateStep() {
  const currentSection = sections[currentStep];
  if (!currentSection) return false;

  const inputs = currentSection.querySelectorAll('input[required], select[required]');
  let isValid = true;

  inputs.forEach((input) => {
    if (!input.checkValidity()) {
      isValid = false;
      input.classList.add('trade-form__input--error');
      input.reportValidity();
    } else {
      input.classList.remove('trade-form__input--error');
    }
  });

  return isValid;
}


// ============================================================
// EVENT LISTENERS
// ============================================================

nextBtn.addEventListener('click', (e) => {
  const isLastStep = currentStep === totalSteps - 1;

  if (!validateStep()) {
    e.preventDefault();
    return;
  }

  if (!isLastStep) {
    e.preventDefault();
    currentStep++;
    updateUI();
  }
});


form.addEventListener('submit', function (e) {
  e.preventDefault();

  // ✅ XSS: Sanitize all fields before any processing
  sanitizeForm(form);

  // Phone formatting
  const phoneField = form.querySelector('input[name="phone"]');

  if (phoneField && phoneField.value.trim() !== "" && phoneField.value.trim() !== "Not Provided") {
    let raw = phoneField.value.replace(/\D/g, '');
    let countryCode = '';
    let number = '';

    if (raw.length === 11 && raw.startsWith('1')) {
      countryCode = '1';
      number = raw.substring(1);
    } else if (raw.length === 10) {
      countryCode = '1';
      number = raw;
    } else {
      countryCode = raw.substring(0, 2);
      number = raw.substring(2);
    }

    const groupedNumber = number.match(/.{1,3}/g)?.join(' ') || number;
    phoneField.value = `+${countryCode} ${groupedNumber}`;
  }

  // Fill empty fields
  const allFields = form.querySelectorAll('input, select, textarea');

  allFields.forEach(field => {
    if (field.type === "file" || field.type === "hidden") return;
    if (!field.value?.trim()) {
      field.value = "Not Provided";
    }
  });

  nextBtn.disabled = true;
  nextBtn.textContent = "Submitting...";

  form.submit();
});


backBtn.addEventListener('click', () => {
  if (currentStep > 0) {
    currentStep--;
    updateUI();
  }
});


addItemBtn.addEventListener('click', () => {
  const newIndex = document.querySelectorAll('.trade-in-item').length;

  tradeInItemsContainer.insertAdjacentHTML(
    'beforeend',
    createTradeInItemHTML(newIndex)
  );

  attachPhotoListeners(tradeInItemsContainer);

  // ✅ XSS: Attach sanitization to the newly added item's inputs
  attachAllSanitizationListeners(tradeInItemsContainer);
});


// ============================================================
// INITIAL RENDER
// ============================================================

renderTradeInItems();
updateUI();
attachPhotoListeners();

// ✅ XSS: Attach sanitization to all inputs present on page load
attachAllSanitizationListeners();